// „Излишък от продажби" — ОПЕРАЦИЯТА, по модела на „Плащане на доставки":
// непокритият излишък от продажби за ВСИЧКИ складове (`GET /inventory/shortages`,
// без период), групиран по склад, с отметка на цял склад (три състояния) или
// на отделен продукт, лепкав футър „Избрани N · бруто" и „Компенсирай с
// доставка". Компенсира се САМО с доставка със ЗАДНА ДАТА: листът „Нова
// доставка" се отваря с попълнените продукти (всеки със своя склад) и дата =
// най-старият отчет-причинител (`compensationStateOf`).
//
// Менюто „⋯" на реда: разбивка по отчети (`ShortageBreakdownModal`) и
// компенсиране само на този продукт. Търсене, филтри Склад/Категория с броячи,
// подредба по име или стойност. Горе — общата лента без период (Excel на
// показаното със сумите по склад — `shortageExcel` · Опресни). Клавиатура:
// ↑/↓ по редовете, Space/Enter отмята, Ctrl/Cmd+Enter = компенсиране.
import { useMemo, useRef, useState } from "react";
import { App, Spin } from "antd";
import {
  CheckOutlined,
  CloudServerOutlined,
  EuroOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useInventorySettings,
  useProducts,
  useShortages,
} from "../../../hooks/useInventory";
import { hasOpenOverlay } from "../../../lib/overlay";
import { exportToExcel } from "../../../utils/excelExport";
import { PageHeader } from "../components/list/PageHeader";
import { ReportToolbar } from "../components/list/ReportToolbar";
import { refreshOf } from "../components/list/reportRefresh";
import {
  EmptyScreen,
  KpiGrid,
  SearchBox,
  SegmentedCounts,
  type KpiCard,
} from "../components/list/ListBits";
import { PRIMARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { CheckBox } from "../components/filters/CheckBox";
import { ActiveFilterChips, FilterButton } from "../components/filters/FilterButton";
import { InventoryTheme } from "../components/InventoryTheme";
import { KbdChip } from "../components/KbdChip";
import { ShortageBreakdownModal } from "./components/ShortageBreakdownModal";
import { ShortageWarehouseGroup } from "./components/ShortageWarehouseGroup";
import { shortageExcelColumns, shortageExcelLabels, shortageExcelRows } from "./components/shortageExcel";
import {
  buildShortageRows,
  categoryOf,
  compensationStateOf,
  filterShortageRows,
  formatDate,
  formatMoney,
  groupShortageRows,
  oldestReportDateOf,
  type ShortageRow,
  type ShortageSort,
} from "./components/shortageShared";

type Filters = { warehouses: string[]; categories: string[] };
const NO_FILTERS: Filters = { warehouses: [], categories: [] };

// Опциите на филтъра с броя продукти за всяка — по ЦЕЛИЯ списък
const optionsOf = (rows: ShortageRow[], valueOf: (row: ShortageRow) => string) => {
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(valueOf(row), (counts.get(valueOf(row)) ?? 0) + 1);
  return [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "bg"))
    .map(([value, count]) => ({ value, label: value, count }));
};

// От общите props на складовите страници (Inventory.tsx) трябва само
// тесният изглед; бутонът за менюто е в PageHeader (useInventoryShell).
type Props = { isNarrow?: boolean };

export const ShortagePage = ({ isNarrow = false }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement | null>(null);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [sort, setSort] = useState<ShortageSort>("name");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [breakdownRow, setBreakdownRow] = useState<ShortageRow | null>(null);

  const productsQuery = useProducts();
  const { data: products = [], isLoading: productsLoading } = productsQuery;
  const { data: settings } = useInventorySettings();
  // Регистрация по ДДС: цените са нето; при НЕрегистрирана фирма бруто = нето.
  const vatRegistered = settings?.vatRegistered ?? true;
  const shortagesQuery = useShortages();
  const { data: shortages = [], isLoading: shortagesLoading, isError } = shortagesQuery;
  const isLoading = productsLoading || shortagesLoading;
  const { refresh, refreshing } = refreshOf(shortagesQuery, productsQuery);

  const productById = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );
  const allRows = useMemo(
    () => buildShortageRows(shortages, productById, vatRegistered),
    [shortages, productById, vatRegistered],
  );
  const rows = useMemo(
    () =>
      filterShortageRows(allRows, search).filter(
        (row) =>
          (!filters.warehouses.length || filters.warehouses.includes(row.warehouseName)) &&
          (!filters.categories.length || filters.categories.includes(categoryOf(row))),
      ),
    [allRows, search, filters],
  );
  const groups = useMemo(() => groupShortageRows(rows, sort), [rows, sort]);

  const totals = useMemo(
    () => ({
      net: rows.reduce((sum, row) => sum + row.quantityNet, 0),
      gross: rows.reduce((sum, row) => sum + row.quantityGross, 0),
    }),
    [rows],
  );
  const kpiCards: KpiCard[] = [
    { Icon: WarningOutlined, label: t("inventoryPage.kpiNegCount"), value: String(rows.length), tone: "danger" },
    { Icon: EuroOutlined, label: t("inventoryPage.kpiNegNet"), value: formatMoney(totals.net) },
    { Icon: EuroOutlined, label: t("inventoryPage.kpiNegGross"), value: formatMoney(totals.gross), tone: "brand" },
  ];

  // Избраните — от ЦЕЛИЯ списък: отметка, скрита от търсене/филтър, остава
  const selectedRows = allRows.filter((row) => selected.has(row.key));
  const selectedGross = selectedRows.reduce((sum, row) => sum + row.quantityGross, 0);
  const selectedDate = oldestReportDateOf(selectedRows);

  const toggle = (keys: string[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  const visibleKeys = rows.map((row) => row.key);
  const visiblePicked = visibleKeys.filter((key) => selected.has(key)).length;
  const allVisiblePicked = visibleKeys.length > 0 && visiblePicked === visibleKeys.length;

  // Компенсиране = доставка със ЗАДНА ДАТА (договорът: DeliveryRouterState)
  const openDelivery = (chosen: ShortageRow[]) =>
    navigate("/inventory/delivery/new", { state: compensationStateOf(chosen) });
  const compensateSelected = () => {
    if (!selectedRows.length) {
      message.info(t("inventoryPage.shortagePickFirst"));
      return;
    }
    openDelivery(selectedRows);
  };

  // Каквото е на екрана (търсене, филтри, подредба) + сумите по склад и общо
  const exportXls = () =>
    exportToExcel({
      filename: "izlishak-ot-prodazhbi",
      sheetName: t("inventoryPage.negativeReport"),
      rows: shortageExcelRows(groups, shortageExcelLabels(t)),
      columns: shortageExcelColumns(t),
    });

  // ↑/↓ по редовете (заглавия на складове и продукти), Ctrl+Enter = компенсиране
  const onKeyDown = (event: React.KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      if (hasOpenOverlay()) return;
      event.preventDefault();
      compensateSelected();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const rowEls = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>("[data-shortage-row]") ?? [],
    );
    const index = rowEls.indexOf(document.activeElement as HTMLElement);
    if (index < 0) return;
    const next = event.key === "ArrowDown" ? index + 1 : index - 1;
    if (next < 0 || next >= rowEls.length) return;
    event.preventDefault();
    rowEls[next].focus();
  };

  const chips = [
    ...filters.warehouses.map((value) => ({
      key: `wh:${value}`,
      label: `${t("inventoryPage.colWarehouse")}: ${value}`,
      onRemove: () =>
        setFilters((current) => ({ ...current, warehouses: current.warehouses.filter((v) => v !== value) })),
    })),
    ...filters.categories.map((value) => ({
      key: `cat:${value}`,
      label: `${t("inventoryPage.colCategory")}: ${value}`,
      onRemove: () =>
        setFilters((current) => ({ ...current, categories: current.categories.filter((v) => v !== value) })),
    })),
  ];

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink" onKeyDown={onKeyDown}>
        <PageHeader
          title={t("inventoryPage.negativeReport")}
          subtitle={t("inventoryPage.shortageSubtitle")}
          right={
            <ReportToolbar
              onExport={exportXls}
              exportDisabled={!rows.length}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spin size="large" />
          </div>
        ) : isError ? (
          <EmptyScreen Icon={CloudServerOutlined} tone="brand" title={t("inventoryPage.errLoad")} />
        ) : allRows.length === 0 ? (
          <EmptyScreen Icon={CheckOutlined} title={t("inventoryPage.noNegative")} />
        ) : (
          <>
            <div
              ref={listRef}
              className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
                isNarrow ? "px-3 pb-6 pt-3.5" : "px-7 pb-8 pt-5"
              }`}
            >
              <KpiGrid cards={kpiCards} />
              <div className="flex flex-wrap items-center gap-2">
                <SearchBox value={search} onChange={setSearch} placeholder={t("inventoryPage.negSearchPh")} />
                <FilterButton
                  label={t("inventoryPage.colWarehouse")}
                  options={optionsOf(allRows, (row) => row.warehouseName)}
                  selected={filters.warehouses}
                  onChange={(values) => setFilters((current) => ({ ...current, warehouses: values }))}
                />
                <FilterButton
                  label={t("inventoryPage.colCategory")}
                  options={optionsOf(allRows, categoryOf)}
                  selected={filters.categories}
                  onChange={(values) => setFilters((current) => ({ ...current, categories: values }))}
                />
                <SegmentedCounts<ShortageSort>
                  value={sort}
                  onChange={setSort}
                  options={[
                    { key: "name", label: t("inventoryPage.shortageSortName") },
                    { key: "value", label: t("inventoryPage.shortageSortValue") },
                  ]}
                />
              </div>
              <ActiveFilterChips
                chips={chips}
                onClearAll={() => {
                  setFilters(NO_FILTERS);
                  setSearch("");
                }}
              />

              {rows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-shade-4 px-[18px] py-6 text-center text-muted">
                  {t("inventoryPage.filterNoMatch")}
                </div>
              ) : (
                <>
                  {/* Всички видими наведнъж (както отметката в заглавието на таблицата) */}
                  <div className="flex flex-wrap items-center gap-3 px-1 text-[13px] text-body">
                    <button
                      type="button"
                      data-shortage-row
                      role="checkbox"
                      aria-checked={allVisiblePicked ? true : visiblePicked ? "mixed" : false}
                      onClick={() => toggle(visibleKeys, !allVisiblePicked)}
                      className="flex cursor-pointer items-center gap-2.5 rounded-md border-none bg-transparent px-1 py-1 text-[13px] text-ink outline-none focus-visible:shadow-[0_0_0_2px_var(--color-brand)]"
                    >
                      <CheckBox checked={allVisiblePicked} indeterminate={!allVisiblePicked && visiblePicked > 0} />
                      {t("inventoryPage.filterSelectAll")}
                    </button>
                    <div className="flex-1" />
                    <span>
                      {t("inventoryPage.shortageTotal")} ·{" "}
                      {t("inventoryPage.shortageProductsCount", { count: rows.length })}
                    </span>
                  </div>
                  {groups.map((group) => (
                    <ShortageWarehouseGroup
                      key={group.warehouseId}
                      group={group}
                      selected={selected}
                      onToggle={toggle}
                      onBreakdown={setBreakdownRow}
                      onCompensateOne={(row) => openDelivery([row])}
                    />
                  ))}
                </>
              )}
            </div>

            <footer
              className={`flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-t border-shade-2 bg-panel ${
                isNarrow
                  ? "px-3.5 py-3 pb-[calc(12px+env(safe-area-inset-bottom,0px)*0.5)]"
                  : "px-6 py-3.5"
              }`}
            >
              <span className="text-body">
                {t("inventoryPage.shortageSelectedGross", { count: selectedRows.length })}
              </span>
              <span className="font-sheet text-xl font-bold text-brand-deep-ink">
                {formatMoney(selectedGross)}
              </span>
              {selectedDate && (
                <span className="text-[12.5px] text-muted">
                  {t("inventoryPage.shortageBackdatedTo", { date: formatDate(selectedDate) })}
                </span>
              )}
              <div className="flex-1" />
              <button
                type="button"
                onClick={compensateSelected}
                className={`${PRIMARY_BUTTON_CLASS} !px-[22px] !py-3 ${
                  selectedRows.length ? "" : "opacity-55"
                } ${isNarrow ? "flex-[1_1_100%]" : ""}`}
              >
                {t("inventoryPage.shortageCompensate")}
                {!isNarrow && <KbdChip>Ctrl+Enter</KbdChip>}
              </button>
            </footer>
          </>
        )}

        <ShortageBreakdownModal
          open={!!breakdownRow}
          onClose={() => setBreakdownRow(null)}
          row={breakdownRow}
          onCompensate={() => {
            const row = breakdownRow;
            setBreakdownRow(null);
            if (row) openDelivery([row]);
          }}
        />
      </div>
    </InventoryTheme>
  );
};

export default ShortagePage;
